// A lookup's hover card: the linked record's compact fields, read through the
// record API (GET /api/objects/:object/:id, behind requireAllowedUser). Reading
// it this way does not count as viewing the record.
import type { ObjectMetadata, RecordPreview, RecordPreviewLoader } from '@olytic/foundation';

import { objects } from '../config/objects';
import type { ObjectKey } from '../navigation';
import { type ObjectRow, parseRecordResponse, recordUrl } from './objectsApi';

/** The most fields a card shows. */
const MAX_FIELDS = 5;
/** Housekeeping fields people never check from a card. */
const SYSTEM = new Set(['created_at', 'updated_at', 'created_by', 'updated_by', 'id']);

const blank = (value: unknown) => value === null || value === undefined || value === '';

/**
 * A record's card: its title, then its highlights or, without them, its first
 * filled-in Details fields (not the name, not system fields).
 */
export function previewOf(metadata: ObjectMetadata<ObjectRow>, row: ObjectRow): RecordPreview {
  const title = metadata.titleOf?.(row) ?? String(row.name ?? '');
  const source = metadata.highlights?.length ? metadata.highlights : (metadata.details ?? []);
  const fields = source
    .filter((f) => f.key !== 'name' && !SYSTEM.has(f.key) && !blank(row[f.key]))
    .slice(0, MAX_FIELDS)
    .map((f) => ({ key: f.key, label: f.label, value: f.render ? f.render(row) : String(row[f.key]) }));
  return { title, objectLabel: metadata.singular, fields };
}

/** Loads the card for a lookup's record; null for an object with no record page here. */
export const recordPreviewLoader =
  (fetcher: (input: string) => Promise<Response>): RecordPreviewLoader =>
  async (object, id) => {
    const metadata = objects[object as ObjectKey];
    if (!metadata) return null;
    const row = await parseRecordResponse(await fetcher(recordUrl(object as ObjectKey, id)));
    return previewOf(metadata, row);
  };
